import {
	addVesselToUserByAdminApi,
	getUserVesselsApi,
	removeVesselFromUserByAdminApi,
} from "api/administration";

import { getVesselFilter, getUserSettingVesselsFilterApi } from "api/vessel";
import { t } from "i18next";
import { CustomError } from "interface";
import { IUserProfileVessel, Vessel } from "interface/vessel";
import React, { FC, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useInfoStore } from "store/error";
import CustomButton from "ui/Button";
import { GrideDateFormatter } from "ui/Date";
import CustomDropDownList from "ui/dropDown";
import { AdminVesselRenderItem } from "ui/dropdownItemList/VesselRenderItem";

import { COLOR, ICON_NAMES } from "utils/constant";
import GridWithGroupPaging from "ui/grid/GridWithGroupPaging";
import { useUserStore } from "store/user";

type VesselItem = IUserProfileVessel & Record<string, any>;

const getVesselValue = (vessel: VesselItem, keys: string[]) => {
	for (const key of keys) {
		const value = vessel[key];
		if (value !== undefined && value !== null && `${value}`.trim() !== "") {
			return `${value}`;
		}
	}

	return "";
};

const getVesselOwnerAccount = (vessel: VesselItem) => {
	return (
		vessel.vesselOwner?.accountBook ||
		vessel.VesselOwner?.AccountBook ||
		vessel.VesselOwner?.accountBook ||
		""
	);
};

const getVesselHullId = (vessel: VesselItem) => {
	return vessel.hullId || vessel.HullId || vessel.id || vessel.Id || null;
};

export const UserVesselsTab: FC<{
	vessels: any[];
	setVessels: (vessel: IUserProfileVessel[]) => void;
}> = ({ vessels, setVessels }) => {
	const [selectedVessel, setSelectedVessel] = useState<Vessel | null>(null);
	const [isMobileView, setIsMobileView] = useState(
		typeof window !== "undefined" ? window.innerWidth < 768 : false,
	);
	let { id } = useParams();
	const { setError } = useInfoStore();
	const { isAdmin, isCompanyAdmin } = useUserStore();

	useEffect(() => {
		const handleResize = () => {
			setIsMobileView(window.innerWidth < 768);
		};

		handleResize();
		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	const adminAddVesselToUser = () => {
		if (!selectedVessel) return;
		addVesselToUserByAdminApi({
			userId: id as any,
			vesselId: selectedVessel.id,
			ledgerAccountNumber: selectedVessel.res as any,
		})
			.then(() => {
				getUserVesselsApi(id as string)
					.then((response) => {
						setVessels(response.data);
						setSelectedVessel(null);
					})
					.catch((error) => {
						console.log(error);
					});
			})
			.catch(() => {
				const err = new CustomError(t("ErrorTitle", "ErrorDescription"));
				setError(err, true);
			});
	};

	const removeVesselFromUser = (vessel: IUserProfileVessel) => {
		if (!vessel) return;
		const vesselItem = vessel as VesselItem;
		const hullId = getVesselHullId(vesselItem);
		const ledgerAccountNumber = getVesselOwnerAccount(vesselItem);
		if (!hullId || !ledgerAccountNumber) return;
		removeVesselFromUserByAdminApi({
			userId: id as any,
			vesselId: hullId,
			ledgerAccountNumber: ledgerAccountNumber as any,
		})
			.then(() => {
				getUserVesselsApi(id as string)
					.then((response) => {
						setVessels(response.data);
					})
					.catch((error) => {
						console.log(error);
					});
			})
			.catch(() => {
				const err = new CustomError(t("ErrorTitle", "ErrorDescription"));
				setError(err, true);
			});
	};

	return (
		<div
			className="user-information-form user-information-split-section"
			style={{
				display: "flex",
				flexDirection: isMobileView ? "column" : "row",
				justifyContent: "space-between",
				gap: "30px",
			}}
		>
			<div
				className="user-information-form-block"
				style={{ width: isMobileView ? "100%" : "50%" }}
			>
				<div style={{ marginTop: "20px" }}>
					<div>
						<CustomDropDownList
							data={[]}
							dataItemKey="id"
							textField="nm"
							label={t("Vessel")}
							value={{
								id: selectedVessel?.id || null,
								nm: selectedVessel?.nm || t("Select") + " " + t("Vessel"),
							}}
							itemRender={AdminVesselRenderItem(false)}
							//defaultItem={isAdmin ? { id: null, nm: t("Select") + " " + t("Vessel") } : undefined}
							onSelect={(data) => {
								setSelectedVessel(data);
							}}
							filterable={isAdmin ? true : false}
							serverFilter={isAdmin ? true : false}
							serverFilterApi={isAdmin ? getVesselFilter : undefined}
							serverApi={isAdmin ? undefined : getUserSettingVesselsFilterApi}
						/>
					</div>
				</div>
				<div
					className="user-information-button-row"
					style={{ marginTop: "20px", display: "flex", gap: "10px" }}
				>
					<CustomButton
						label={t("Add") + " " + t("Vessel")}
						onClick={() => adminAddVesselToUser()}
						disabled={!selectedVessel}
						bgColor={COLOR.Green}
						color="white"
						btnStyle={isMobileView ? { width: "100%" } : undefined}
					/>
				</div>
			</div>
			<div
				className="user-information-form-block"
				style={{
					width: isMobileView ? "100%" : "50%",
					display: "flex",
					justifyContent: "flex-start",
					flexDirection: "column",
					gap: "10px",
				}}
			>
				<div style={{ marginBottom: "-30px", paddingTop: "10px" }}>
					{t("Vessel")}
				</div>
				<div
					className="user-information-grid-shell"
					style={{ marginTop: "20px" }}
				>
					{isMobileView ? (
						<UserVesselMobileList
							vessels={vessels as IUserProfileVessel[]}
							removeVesselFromUser={removeVesselFromUser}
						/>
					) : (
						<VesselGrid
							vessels={vessels}
							removeVesselFromUser={removeVesselFromUser}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

const UserVesselMobileList: FC<{
	vessels: IUserProfileVessel[];
	removeVesselFromUser: (vessel: IUserProfileVessel) => void;
}> = ({ vessels, removeVesselFromUser }) => {
	if (vessels.length === 0) {
		return <div className="user-information-mobile-empty">{t("NoRows")}</div>;
	}

	return (
		<div className="user-information-mobile-list">
			{vessels.map((vessel, index) => {
				const vesselItem = vessel as VesselItem;
				const name =
					getVesselValue(vesselItem, ["description", "Description"]) || "-";
				const registrationNumber =
					getVesselValue(vesselItem, [
						"registrationNumber",
						"RegistrationNumber",
					]) || "-";
				const accountBook = getVesselOwnerAccount(vesselItem) || "-";
				const key = `${getVesselHullId(vesselItem) || registrationNumber}-${index}`;

				return (
					<div key={key} className="user-information-mobile-card">
						<div className="user-information-mobile-card-header user-information-mobile-card-header--simple">
							<div className="user-information-mobile-card-title">{name}</div>
							<div className="user-information-mobile-card-subtitle">
								{registrationNumber}
							</div>
						</div>
						<div className="user-information-mobile-card-body">
							<div>
								<span>{t("VesselAccountLedger")}</span>
								<strong>{accountBook}</strong>
							</div>
							<div>
								<span>{t("To")}</span>
								<div className="user-information-mobile-card-value">
									{vesselItem.toDate || vesselItem.ToDate ? (
										<GrideDateFormatter
											date={vesselItem.toDate || vesselItem.ToDate}
											formatType={"dd.MM.yyyy"}
											noTd={true}
										/>
									) : (
										"-"
									)}
								</div>
							</div>
						</div>
						<div className="user-information-mobile-card-actions">
							<button
								type="button"
								className="user-information-mobile-icon-btn"
								onClick={() => removeVesselFromUser(vessel)}
								title={t("Delete")}
							>
								<img src={`/icon/${ICON_NAMES.Delete}.svg`} alt={t("Delete")} />
							</button>
						</div>
					</div>
				);
			})}
		</div>
	);
};

const VesselGrid: FC<{
	vessels: IUserProfileVessel[];
	removeVesselFromUser: (vessel: IUserProfileVessel) => void;
}> = ({ vessels, removeVesselFromUser }) => {
	return (
		<GridWithGroupPaging
			// style={{ overflowY: "hidden" }}
			// data={vessels}
			className="hide-virtual-scroll"
			pageable={false}
			gridData={vessels}
			itemAboveHeight={230}
			gridColumns={[
				{
					field: "vesselOwner.accountBook",
					title: t("VesselAccountLedger"),
					width: 120,
				},
				{ field: "registrationNumber", title: t("VesselRegistrationNo") },
				{ field: "description", title: t("VesselName") },
				{
					field: "toDate",
					title: t("ValidTo"),
					width: 150,
					cells: {
						data: (props) => (
							<GrideDateFormatter
								date={props.dataItem.toDate}
								formatType={"dd.MM.yyyy"}
							/>
						),
					},
				},
				{ field: "smsPhoneNumber", title: t("SMS"), width: 150 },
				{
					width: 100,
					cells: {
						data: (props) => {
							const { dataItem } = props;
							return (
								<td
									style={{
										display: "flex",
										justifyContent: "flex-end",
										paddingRight: "10px",
										height: 38,
									}}
								>
									<CustomButton
										label={t("")}
										btnStyle={{
											// backgroundColor: "transparent",
											// border: "1px solid #006980",
											height: "26px",
											// paddingBottom: "2px",
											// margin: "2px",
										}}
										iconName={ICON_NAMES.Delete}
										onClick={() => {
											removeVesselFromUser(dataItem);
										}}
									/>
								</td>
							);
						},
					},
				},
			]}
		/>
	);
};
